import type { Dispatch } from '@reduxjs/toolkit';
import { monthlyTimelineActions } from 'app/components/MonthlyTimeline/store/monthlyTimelineSlice';
import { homeActions } from 'app/pages/Home/slice';
import { milestonesGridActions } from 'app/pages/MilestonesGrid/slice';
import { applyMustWinViewStateFromParams } from 'app/utils/applyMustWinViewState';
import { persistSavedViewPreferences } from 'app/utils/persistSavedViewPreferences';
import {
  isMilestonesRoute,
  isRoadmapRoute,
  type ViewStateParams
} from 'utils/viewStateDeepLink';

type ApplyViewStateOptions = {
  onMilestonesBetaView?: (showBetaView: boolean) => void;
  onRoadmapIssueIds?: (issueIds: string[]) => void;
  categoryToProductMap?: Record<string, string[]>;
  /** When true, apply Redux state only — do not write to localStorage (shared-view tab). */
  skipPreferencePersistence?: boolean;
};

/**
 * Clear all filters and re-apply the full saved view snapshot from URL params.
 * Used when the user clicks a saved view name in the sidebar.
 */
export function applySavedViewState(
  dispatch: Dispatch,
  pathname: string,
  params: ViewStateParams,
  options: ApplyViewStateOptions = {}
) {
  if (isRoadmapRoute(pathname)) {
    if (params.timelineViewMode) {
      dispatch(
        monthlyTimelineActions.setTimelineViewMode(params.timelineViewMode)
      );
    }
    if (params.timelineIconsVisible !== undefined) {
      dispatch(
        monthlyTimelineActions.setTimelineIconsVisible(
          params.timelineIconsVisible
        )
      );
    }

    if (params.issueIds && params.issueIds.length > 0) {
      options.onRoadmapIssueIds?.(params.issueIds);
    }
  }

  // Atomic clear + apply for filters and view toggles encoded in the saved link.
  dispatch(homeActions.replaceViewStateFromSavedView(params));

  if (params.startDate && params.endDate) {
    dispatch(homeActions.loadReleaseData({}));
  }

  if (isRoadmapRoute(pathname) && params.mustWin !== undefined) {
    applyMustWinViewStateFromParams(
      dispatch,
      params,
      options.categoryToProductMap ?? {},
      { persistPreferences: false }
    );
  }

  if (isRoadmapRoute(pathname)) {
    dispatch(homeActions.loadHierarchyFilters({}));
  }

  if (isMilestonesRoute(pathname)) {
    dispatch(milestonesGridActions.applyViewStateFromUrl(params));

    if (params.showBetaView !== undefined) {
      options.onMilestonesBetaView?.(params.showBetaView);
    }
  }

  // Replace localStorage with the saved view snapshot so refresh restores it.
  if (!options.skipPreferencePersistence) {
    persistSavedViewPreferences(params, pathname);
  }

  dispatch(homeActions.bumpSavedViewApplyToken());
}

/** @deprecated Use applySavedViewState for sidebar saved-view navigation. */
export function applyViewStateFromNavigation(
  dispatch: Dispatch,
  pathname: string,
  params: ViewStateParams,
  options: ApplyViewStateOptions = {}
) {
  applySavedViewState(dispatch, pathname, params, options);
}
